import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { getDirectoryModuleIcon } from '@/shared/ui/module-icons';
import type { SearchComboboxOption } from '@/shared/ui/search-combobox';
import {
  approvalConfigApi,
  type ApprovalConfigOption,
  type ApprovalModuleOption,
} from '../api/approval-config.api';
import { useFormBuilderToast } from './useFormBuilderToast';

export interface ApprovalConfigSelectOption {
  id: string | number;
  name: string;
  disabled?: boolean;
}

export interface UseApprovalConfigOptionsReturn {
  moduleOptions: ComputedRef<SearchComboboxOption[]>;
  moduleSearchTerm: Ref<string>;
  filteredModuleOptions: ComputedRef<SearchComboboxOption[]>;
  recordOriginOptions: ComputedRef<ApprovalConfigSelectOption[]>;
  recordTypeOptions: ComputedRef<ApprovalConfigOption[]>;
  isLoadingModules: Ref<boolean>;
  isLoadingRecordOrigins: Ref<boolean>;
  isLoadingRecordTypes: Ref<boolean>;
  loadInitialOptions: () => Promise<void>;
  loadRecordTypes: (recordOriginId: number | '') => Promise<void>;
  findRecordTypeName: (recordTypeId: number | '') => string;
}

export function useApprovalConfigOptions(): UseApprovalConfigOptionsReturn {
  const { showErrorToast } = useFormBuilderToast();

  const modules = ref<ApprovalModuleOption[]>([]);
  const recordOrigins = ref<ApprovalConfigOption[]>([]);
  const recordTypes = ref<ApprovalConfigOption[]>([]);
  const moduleSearchTerm = ref('');
  const isLoadingModules = ref(false);
  const isLoadingRecordOrigins = ref(false);
  const isLoadingRecordTypes = ref(false);
  let recordTypesRequestSequence = 0;

  function reportError(title: string) {
    showErrorToast({ title, type: 'error', hideIcon: true });
  }

  const moduleOptions = computed<SearchComboboxOption[]>(() =>
    modules.value.map((module) => ({
      value: String(module.id),
      label: module.name,
      icon: getDirectoryModuleIcon(module.iconId ?? undefined),
    }))
  );

  const filteredModuleOptions = computed<SearchComboboxOption[]>(() => {
    const term = moduleSearchTerm.value.trim().toLowerCase();
    if (term === '') return moduleOptions.value;
    return moduleOptions.value.filter((option) => option.label.toLowerCase().includes(term));
  });

  const recordOriginOptions = computed<ApprovalConfigSelectOption[]>(() => {
    if (isLoadingRecordOrigins.value) {
      return [{ id: '', name: 'Carregando origens...', disabled: true }];
    }
    if (recordOrigins.value.length > 0) return recordOrigins.value;
    return [{ id: '', name: 'Nenhuma origem encontrada', disabled: true }];
  });

  // Sorted by name so the radios read Editável → Editável (IF/API) → Upload, as in the design.
  const recordTypeOptions = computed(() =>
    [...recordTypes.value].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  );

  async function loadModules() {
    isLoadingModules.value = true;
    try {
      modules.value = await approvalConfigApi.listModules();
    } catch {
      modules.value = [];
      reportError('Erro ao carregar os módulos, tente novamente.');
    } finally {
      isLoadingModules.value = false;
    }
  }

  async function loadRecordOrigins() {
    isLoadingRecordOrigins.value = true;
    try {
      recordOrigins.value = await approvalConfigApi.listRecordOrigins();
    } catch {
      recordOrigins.value = [];
      reportError('Erro ao carregar as origens do registro, tente novamente.');
    } finally {
      isLoadingRecordOrigins.value = false;
    }
  }

  async function loadInitialOptions() {
    await Promise.all([loadModules(), loadRecordOrigins()]);
  }

  async function loadRecordTypes(recordOriginId: number | '') {
    // Origins can be switched faster than their requests answer; only the newest request may
    // publish, otherwise a slow earlier response would list the types of a no longer selected origin.
    const sequence = ++recordTypesRequestSequence;
    recordTypes.value = [];

    if (recordOriginId === '') {
      isLoadingRecordTypes.value = false;
      return;
    }

    isLoadingRecordTypes.value = true;
    try {
      const types = await approvalConfigApi.listRecordTypes(recordOriginId);
      if (sequence !== recordTypesRequestSequence) return;
      recordTypes.value = types;
    } catch {
      if (sequence !== recordTypesRequestSequence) return;
      recordTypes.value = [];
      reportError('Erro ao carregar os tipos do registro, tente novamente.');
    } finally {
      if (sequence === recordTypesRequestSequence) isLoadingRecordTypes.value = false;
    }
  }

  function findRecordTypeName(recordTypeId: number | ''): string {
    if (recordTypeId === '') return '';
    return recordTypes.value.find((type) => type.id === recordTypeId)?.name ?? '';
  }

  return {
    moduleOptions,
    moduleSearchTerm,
    filteredModuleOptions,
    recordOriginOptions,
    recordTypeOptions,
    isLoadingModules,
    isLoadingRecordOrigins,
    isLoadingRecordTypes,
    loadInitialOptions,
    loadRecordTypes,
    findRecordTypeName,
  };
}
